/* eslint-disable import/no-cycle */
/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */
import seed from 'seed-random'
import BaseModel from '@/utils/store/BaseModel'
import CollectionItem, { COLORS, SHAPES } from '@/store/models/collectionItems/CollectionItem'
import ResourceTag from '@/store/models/collectionItems/ResourceTag'
import User from '@/store/models/user/User'
import CollectionMember from '@/store/models/collections/CollectionMember'
import CollectionShareLink from '@/store/models/collections/CollectionShareLink'
import { ITEM_LAYOUT_STYLE, ALL_ITEM_LAYOUT_STYLES, ResourceType, COLLECTION_NAME_DISPLAY_MAX_LENGTH } from '@/utils/constants'
import truncateString from '@/utils/truncateString'

// Import all placeholder images statically
import placeholder1Thumbnail from '@/assets/img/placeholders/1-thumbnail.webp'
import placeholder2Thumbnail from '@/assets/img/placeholders/2-thumbnail.webp'
import placeholder3Thumbnail from '@/assets/img/placeholders/3-thumbnail.webp'
import placeholder4Thumbnail from '@/assets/img/placeholders/4-thumbnail.webp'
import placeholder5Thumbnail from '@/assets/img/placeholders/5-thumbnail.webp'

// Create a mapping object for placeholders
const PLACEHOLDER_THUMBNAILS = {
  1: placeholder1Thumbnail,
  2: placeholder2Thumbnail,
  3: placeholder3Thumbnail,
  4: placeholder4Thumbnail,
  5: placeholder5Thumbnail,
}

export const SHOW_COUNT = 25

class Collection extends BaseModel {
  static entity = 'collections'

  static fields() {
    return {
      ...super.fields(),
      name: this.string(),
      description: this.attr(null),
      defaultView: this.string(ITEM_LAYOUT_STYLE.cards),
      layouts: this.attr(() => [...ALL_ITEM_LAYOUT_STYLES]),
      defaultSorting: this.attr(),
      // stats
      bookmarkCount: this.number(),
      itemsCount: this.number(0),
      memberCount: this.number(),
      thumbnails: this.attr([]),
      thumbnailsV2: this.attr([]),
      // 0–2 item copies shown beside the description (serialized like list items).
      // Factory fn so ORM inserts from payloads without the field keep an array.
      introItems: this.attr(() => []),
      image: this.attr(null),
      createdAt: this.attr().nullable(),
      depth: this.number(0),
      parent: this.string(null),
      subCollectionCount: this.number(0),
      shared: this.boolean(false),
      isPublic: this.boolean(false),
      shortId: this.string(null),
      containsBookmark: this.boolean(false),
      subCollectionPosition: this.string('first'),
      pinPosition: this.attr(null),
      numberOfPinnedItems: this.number(0),
      parentIsPublic: this.attr(null),
      // owner's custom branding (header banner + background), shown to viewers
      // of this collection. Attached by the private getId response; owner may
      // differ from the viewer on shared collections.
      ownerBranding: this.attr(null),
      // relationships
      shareLink: this.hasOne(CollectionShareLink, 'collectionId'),
      ownerId: this.string(),
      owner: this.belongsTo(User, 'ownerId'),
      items: this.hasMany(CollectionItem, 'ownerId'),
      tags: this.hasMany(ResourceTag, 'ownerId'),
      members: this.hasMany(CollectionMember, 'collectionId'),
      users: this.belongsToMany(User, CollectionMember, 'collectionId', 'userId'),
      subCollections: this.hasMany(Collection, 'parent'),
      // Preserves API response order for VuexORM queries
      _sortPosition: this.attr(null),
    }
  }

  get isNested() {
    return this.parent && this.parent !== 'null'
  }

  get truncatedName() {
    return truncateString(this.name, COLLECTION_NAME_DISPLAY_MAX_LENGTH)
  }

  get nameLower() {
    return this.name.toLowerCase()
  }

  get hasSubCollections() {
    return this.subCollectionCount > 0
  }

  get teaserImageUrl() {
    // Custom user-uploaded teaser. Reuses the bookmarkthumbnail CDN class (same 16:9 sizing).
    return this.image ? `${CONFIG.cdnUrl}/${this.image}?class=bookmarkthumbnail` : null
  }

  get thumbnailBookmarks() {
    // MVP+ this is quite hacky we could also just insert the bookmarks...
    if (!this.thumbnails.length) return []

    return this.thumbnails.map(bookmark => {
      const rand = seed(bookmark._id)()
      const colorClasses = COLORS[Math.floor(rand * COLORS.length)]
      const shape = SHAPES[Math.floor(rand * SHAPES.length)]

      const thumbnail = bookmark.image ?? bookmark.webPage?.image
      let thumbnailUrl = null
      if (thumbnail) {
        thumbnailUrl = `${CONFIG.cdnUrl}/${thumbnail}?class=bookmarkthumbnail`
      } else {
        // Use the mapping object instead of dynamic require
        thumbnailUrl = PLACEHOLDER_THUMBNAILS[shape]
      }

      return {
        _id: bookmark._id,
        colorClasses,
        thumbnailUrl,
        image: thumbnail,
      }
    })
  }

  get thumbnailItemsV2() {
    if (!this.thumbnailsV2?.length) return []

    return this.thumbnailsV2.map(item => {
      const rand = seed(item._id)()

      // Handle notes
      if (item.resourceType === ResourceType.NOTE) {
        return {
          _id: item._id,
          resourceType: ResourceType.NOTE,
          title: item.title,
          color: item.color,
          contentPreview: item.contentPreview,
          contentPreviewPlainText: item.contentPreviewPlainText,
        }
      }

      // Handle bookmarks (default behavior for backward compatibility)
      const colorClasses = COLORS[Math.floor(rand * COLORS.length)]
      const shape = SHAPES[Math.floor(rand * SHAPES.length)]

      const thumbnail = item.image ?? item.webPage?.image
      let thumbnailUrl = null
      if (thumbnail) {
        thumbnailUrl = `${CONFIG.cdnUrl}/${thumbnail}?class=bookmarkthumbnail`
      } else {
        thumbnailUrl = PLACEHOLDER_THUMBNAILS[shape]
      }

      return {
        _id: item._id,
        resourceType: ResourceType.BOOKMARK,
        colorClasses,
        thumbnailUrl,
        image: thumbnail,
      }
    })
  }
}

export default Collection
